/**
 * Huaca brand primitives. The mark is the huaca pyramid — an isometric 3-tier ziggurat (the sacred
 * keeper-structure that echoes Huaca's gatekeeper role) — and is the shared spine every module
 * co-brands with. Rendered in the accent color via `currentColor` on `.huaca-mark`.
 *
 * Two-tier brand system: this simplified glyph is the UI mark (it stays legible from 20px up); the
 * full detailed pyramid is reserved for OG images, app icons 192/512 and marketing.
 *
 * Depth comes from `fillOpacity` faces (top 1.0 · right .7 · left .45), never a gradient, so the
 * mark themes on color alone. Paint order is bottom tier → top tier: each tier sits on the one
 * below. Geometrically symmetric about x=12, but the shading is directional — as a brand mark it is
 * NEVER RTL-mirrored.
 */

export function HuacaMark({ size = 22, className }: { size?: number; className?: string }) {
  return (
    <svg
      className={className ? `huaca-mark ${className}` : 'huaca-mark'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 13 L12 8.5 L21 13 L12 17.5 Z" />
      <path d="M3 13 L12 17.5 L12 20.5 L3 16 Z" fillOpacity={0.45} />
      <path d="M21 13 L12 17.5 L12 20.5 L21 16 Z" fillOpacity={0.7} />
      <path d="M6 10 L12 7 L18 10 L12 13 Z" />
      <path d="M6 10 L12 13 L12 16 L6 13 Z" fillOpacity={0.45} />
      <path d="M18 10 L12 13 L12 16 L18 13 Z" fillOpacity={0.7} />
      <path d="M8.5 7 L12 5.25 L15.5 7 L12 8.75 Z" />
      <path d="M8.5 7 L12 8.75 L12 11.75 L8.5 10 Z" fillOpacity={0.45} />
      <path d="M15.5 7 L12 8.75 L12 11.75 L15.5 10 Z" fillOpacity={0.7} />
    </svg>
  );
}

/** Standalone pyramid + "huaca" wordmark for the login card / marketing surfaces. */
export function HuacaWordmark({ size = 26 }: { size?: number }) {
  return (
    <span className="huaca-wordmark">
      <HuacaMark size={size} />
      huaca
    </span>
  );
}
